import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Loader, CheckCircle } from 'lucide-react';
import { generateOnlineAdmissionReceipt } from '@/utils/onlineAdmissionReceiptGenerator';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdmissionReceipt = () => {
  const { applicationId } = useParams();
  const [application, setApplication] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplication();
  }, [applicationId]);

  const fetchApplication = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/online-admissions/${applicationId}`);
      setApplication(response.data);
      
      // Fetch tenant info for proper branding in receipt
      if (response.data.tenant_id) {
        try {
          const tenantResponse = await axios.get(`${API_URL}/api/tenants/${response.data.tenant_id}/public-info`);
          setTenantInfo(tenantResponse.data);
        } catch (tenantError) {
          console.error('Error fetching tenant info:', tenantError);
          // Try alternative endpoint
          try {
            const altResponse = await axios.get(`${API_URL}/api/online-admission/public/tenant-info/${response.data.tenant_id}`);
            setTenantInfo(altResponse.data);
          } catch (altError) {
            console.error('Error fetching tenant info from alt endpoint:', altError);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching application:', error);
      alert('Failed to load receipt');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (application) {
      // Convert tenantInfo to collegeInfo format for receipt
      const collegeInfoForReceipt = tenantInfo ? {
        college_name: tenantInfo.name || tenantInfo.college_name,
        name: tenantInfo.name || tenantInfo.college_name,
        address: tenantInfo.address,
        city: tenantInfo.city,
        state: tenantInfo.state,
        pincode: tenantInfo.pincode || tenantInfo.pin_code,
        phone: tenantInfo.phone || tenantInfo.mobile,
        email: tenantInfo.email,
        logo_url: tenantInfo.logo_url || tenantInfo.logo
      } : null;
      
      await generateOnlineAdmissionReceipt(application, {}, collegeInfoForReceipt);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 text-blue-600 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading receipt...</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-red-600">Receipt not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Admission Receipt</h1>
              <p className="text-gray-600">Payment Successful</p>
            </div>

            <div className="space-y-6">
              {/* Receipt Details */}
              <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Receipt Number</p>
                    <p className="text-lg font-bold text-gray-900">{application.receipt_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Application Number</p>
                    <p className="text-lg font-bold text-gray-900">{application.application_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Amount Paid</p>
                    <p className="text-lg font-bold text-green-700">₹{application.admission_fee?.toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Payment Date</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatDate(application.payment_date)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Student Details */}
              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3">Student Details</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">Name</p>
                    <p className="font-medium">{application.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Email</p>
                    <p className="font-medium">{application.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Phone</p>
                    <p className="font-medium">{application.phone}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Course</p>
                    <p className="font-medium">{application.course_name}</p>
                  </div>
                </div>
              </div>

              {/* Download Button */}
              <div className="text-center">
                <Button
                  onClick={handleDownload}
                  className="bg-blue-600 hover:bg-blue-700 text-lg py-6 px-8"
                >
                  <Download size={20} className="mr-2" />
                  Download Receipt PDF
                </Button>
              </div>

              {/* Information */}
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Important:</strong> Your application will be reviewed by our admission team. 
                  You will receive an email notification once your application is approved.
                  Please keep this receipt for future reference.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdmissionReceipt;
